import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';

import { ROOM_TYPE_OPTIONS } from '../../constants/style-settings';
import { RoomTypeSelectionContent } from './room-type-selection-content';
import { StyleSettings } from './style-settings';

interface RoomTypeSettingsProps {
  roomType?: ROOM_TYPE_OPTIONS;
  onUpdate: (key: keyof StyleSettings, val: ROOM_TYPE_OPTIONS) => void;
}

export const RoomTypeSettings = ({ roomType, onUpdate }: RoomTypeSettingsProps) => {
  const { t } = useTranslation();
  const isMobileView = !useResizeObserver(640);

  const [isOpened, setIsOpened] = useState(false);
  const [selected, setSelected] = useState<ROOM_TYPE_OPTIONS | undefined>();

  const handleSelect = (option: ROOM_TYPE_OPTIONS) => {
    setSelected(option);
    setIsOpened(false);
  };

  useEffect(() => {
    if (isOpened) {
      if (roomType) {
        setSelected(roomType);
      }
    } else if (selected) {
      onUpdate('roomType', selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomType, isOpened]);

  return (
    <Popover onOpenChange={setIsOpened} open={isOpened}>
      <PopoverTrigger asChild>
        <div>
          <div
            className={cn(
              'flex min-h-[40px] w-full cursor-pointer items-center justify-between gap-x-4 rounded-sm bg-[#EFF4F5] px-4 sm:hidden lg:flex',
              isOpened ? 'bg-primary-light' : 'bg-[#EFF4F5]'
            )}
          >
            <span
              className={cn('flex-1 text-left text-sm font-semibold', isOpened && 'text-white')}
            >
              {t('roomType')}
              <span aria-hidden>*</span>
              <span className="sr-only">{t('required')}</span>
            </span>
            <span className={cn('text-right', isOpened && 'text-white')}>
              {!!roomType && t(roomType)}
            </span>
          </div>
          <div
            className={cn(
              'hidden min-h-[26px] w-full cursor-pointer items-center justify-center rounded-sm bg-[#EFF4F5] px-2 sm:flex lg:hidden lg:px-4',
              isOpened ? 'bg-primary-light' : 'bg-[#EFF4F5]'
            )}
          >
            {roomType ? (
              <span className={cn('text-center text-xs', isOpened && 'text-white')}>
                {t(roomType)}
              </span>
            ) : (
              <span
                className={cn('flex-1text-center text-xs font-semibold', isOpened && 'text-white')}
              >
                {t('roomType')}
                <span aria-hidden>*</span>
                <span className="sr-only">{t('required')}</span>
              </span>
            )}
          </div>
        </div>
      </PopoverTrigger>
      {isMobileView ? <PopoverAnchor className="fixed top-0" /> : null}
      <PopoverContent
        className="h-screen w-screen overflow-hidden rounded-sm p-0 sm:h-auto sm:max-w-[476px]"
        side={isMobileView ? 'bottom' : 'right'}
        sideOffset={isMobileView ? 0 : 10}
      >
        <RoomTypeSelectionContent roomType={selected} handleUpdateRoomType={handleSelect} />
      </PopoverContent>
    </Popover>
  );
};
